import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  IconButton,
  useTheme,
  useMediaQuery,
  Skeleton,
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';

interface ImageCarouselProps {
  images: string[];
  alt?: string;
  aspectRatio?: number;
  showDots?: boolean;
  showArrows?: boolean;
  autoPlay?: boolean;
  autoPlayInterval?: number;
  onImageClick?: (index: number) => void;
  borderRadius?: number;
  loading?: boolean;
}

const ImageCarousel: React.FC<ImageCarouselProps> = ({
  images,
  alt = 'Property image',
  aspectRatio = 1, // 1:1 aspect ratio like Airbnb
  showDots = true,
  showArrows = true,
  autoPlay = false,
  autoPlayInterval = 5000,
  onImageClick,
  borderRadius = 12,
  loading = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [imageLoadStates, setImageLoadStates] = useState<Record<number, boolean>>({});
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  
  const containerRef = useRef<HTMLDivElement>(null);
  const autoPlayRef = useRef<NodeJS.Timeout | null>(null);

  // Handle image loading states
  const handleImageLoad = useCallback((index: number) => {
    setImageLoadStates(prev => ({ ...prev, [index]: true }));
  }, []);

  // Navigation functions
  const goToNext = useCallback(() => {
    if (images.length === 0) return;
    setCurrentIndex(prev => (prev + 1) % images.length);
  }, [images.length]);

  const goToPrevious = useCallback(() => {
    if (images.length === 0) return;
    setCurrentIndex(prev => (prev - 1 + images.length) % images.length);
  }, [images.length]);

  const goToSlide = useCallback((index: number) => {
    setCurrentIndex(index);
  }, []);

  // Auto-play functionality
  useEffect(() => {
    if (autoPlay && !isHovered && images.length > 1) {
      autoPlayRef.current = setInterval(goToNext, autoPlayInterval);
    } else if (autoPlayRef.current) {
      clearInterval(autoPlayRef.current);
      autoPlayRef.current = null;
    }

    return () => {
      if (autoPlayRef.current) {
        clearInterval(autoPlayRef.current);
      }
    };
  }, [autoPlay, isHovered, images.length, goToNext, autoPlayInterval]);

  // Touch/swipe handling
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      goToNext();
    } else if (isRightSwipe) {
      goToPrevious();
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!containerRef.current?.contains(document.activeElement)) return;
      
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goToPrevious();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        goToNext();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [goToNext, goToPrevious]);

  // Handle empty images or loading state
  if (loading || images.length === 0) {
    return (
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          paddingBottom: `${100 / aspectRatio}%`,
          borderRadius: `${borderRadius}px`,
          overflow: 'hidden',
          backgroundColor: 'grey.100',
        }}
      >
        <Skeleton
          variant="rectangular"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            borderRadius: `${borderRadius}px`,
          }}
        />
      </Box>
    );
  }

  const hasMultipleImages = images.length > 1;

  return (
    <Box
      ref={containerRef}
      sx={{
        position: 'relative',
        width: '100%',
        paddingBottom: `${100 / aspectRatio}%`,
        borderRadius: `${borderRadius}px`,
        overflow: 'hidden',
        cursor: onImageClick ? 'pointer' : 'default',
        '&:focus': {
          outline: `2px solid ${theme.palette.primary.main}`,
          outlineOffset: 2,
        },
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onClick={() => onImageClick?.(currentIndex)}
      tabIndex={onImageClick ? 0 : -1}
      role={onImageClick ? 'button' : undefined}
      aria-label={onImageClick ? `View ${alt}` : undefined}
    >
      {/* Images */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          display: 'flex',
          transform: `translateX(-${currentIndex * 100}%)`,
          transition: 'transform 0.3s ease-in-out',
        }}
      >
        {images.map((image, index) => (
          <Box
            key={index}
            sx={{
              position: 'relative',
              minWidth: '100%',
              height: '100%',
            }}
          >
            {/* Loading skeleton */}
            {!imageLoadStates[index] && (
              <Skeleton
                variant="rectangular"
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                }}
              />
            )}
            
            {/* Actual image */}
            <Box
              component="img"
              src={image}
              alt={`${alt} ${index + 1}`}
              onLoad={() => handleImageLoad(index)}
              onError={() => handleImageLoad(index)} // Still mark as loaded even on error
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: imageLoadStates[index] ? 'block' : 'none',
              }}
            />
          </Box>
        ))}
      </Box>

      {/* Navigation Arrows */}
      {showArrows && hasMultipleImages && (isHovered || isMobile) && (
        <>
          {/* Previous Arrow */}
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              goToPrevious();
            }}
            sx={{
              position: 'absolute',
              left: 8,
              top: '50%',
              transform: 'translateY(-50%)',
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              color: 'text.primary',
              width: 32,
              height: 32,
              zIndex: 2,
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 1)',
              },
              '&:focus': {
                backgroundColor: 'rgba(255, 255, 255, 1)',
              },
            }}
            aria-label="Previous image"
          >
            <ChevronLeftIcon fontSize="small" />
          </IconButton>

          {/* Next Arrow */}
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              goToNext();
            }}
            sx={{
              position: 'absolute',
              right: 8,
              top: '50%',
              transform: 'translateY(-50%)',
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              color: 'text.primary',
              width: 32,
              height: 32,
              zIndex: 2,
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 1)',
              },
              '&:focus': {
                backgroundColor: 'rgba(255, 255, 255, 1)',
              },
            }}
            aria-label="Next image"
          >
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </>
      )}

      {/* Dot Indicators */}
      {showDots && hasMultipleImages && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 12,
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            gap: 0.5,
            zIndex: 2,
          }}
        >
          {images.map((_, index) => (
            <Box
              key={index}
              onClick={(e) => {
                e.stopPropagation();
                goToSlide(index);
              }}
              sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                backgroundColor: index === currentIndex 
                  ? 'rgba(255, 255, 255, 1)' 
                  : 'rgba(255, 255, 255, 0.5)',
                cursor: 'pointer',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                  transform: 'scale(1.2)',
                },
              }}
              role="button"
              tabIndex={0}
              aria-label={`Go to image ${index + 1}`}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  e.stopPropagation();
                  goToSlide(index);
                }
              }}
            />
          ))}
        </Box>
      )}

      {/* Image Counter (for screen readers) */}
      <Box
        sx={{
          position: 'absolute',
          top: -9999,
          left: -9999,
        }}
        aria-live="polite"
        aria-atomic="true"
      >
        Image {currentIndex + 1} of {images.length}
      </Box>
    </Box>
  );
};

export default ImageCarousel;